import { Component } from 'react';
import PropTypes from 'prop-types';
import { find, propEq } from 'ramda';
import classNames from 'classnames';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import s from './SelectFieldEditor.module.scss';
import Icon from '../icon/Icon.jsx';

/**
 * The SelectFieldEditor is the React SelectComponent with added functionality. It checks whether or not the dropdown is creatable, can add icons, and
 * can enable or disable fields that are passed down.
 *
 * Static Labels and Buttons
 * @param {string} className: Class
 * @param {string} label: String that appears in the dropdown
 * @param {array} options: The label and the value for each selection
 * @param {boolean} creatable: Checks to see if field accepts the creation of new fields
 * @param {function} onValueSelected: Gets clicked value
 * @param {string} selectedValue: The clicked on value from the dropdown
 * @param {boolean} categoryIcons: The icons next to the fields
 * @param {boolean} disabled: Whether field is clickable or not
 *
 */

export default class SelectFieldEditor extends Component {
    static propTypes = {
        className: PropTypes.string,
        label: PropTypes.string,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
            }),
        ),
        creatable: PropTypes.bool,
        onValueSelected: PropTypes.func,
        selectedValue: PropTypes.string,
        categoryIcons: PropTypes.bool,
        disabled: PropTypes.bool,
    };

    static defaultProps = {
        options: [
            { value: 'one', label: 'One' },
            { value: 'two', label: 'Two' },
        ],
        creatable: false,
        onValueSelected: () => {},
        disabled: false,
    };

    /**
     * Updates the props with the current option
     *
     */
    handleChange = selectedOption => {
        selectedOption ? this.props.onValueSelected(selectedOption.value) : this.props.onValueSelected('');
    };

    /**
     * Adds additional options to the dropdown, like creatability
     *
     */
    enhancedOptions = () => {
        const { selectedValue, options, creatable } = this.props;

        if (selectedValue && creatable && !find(propEq(selectedValue, 'value'), options)) {
            return [{ value: selectedValue, label: selectedValue }, ...options];
        }
        return options;
    };

    /**
     * Inserts a <div> tag containing the corresponding icon for a field
     *
     */
    iconValueRenderer = item => {
        return (
            <div className={s.iconOption}>
                <Icon url={item.badge} size={20} />
                <span className={s.iconLabel}>{item.label}</span>
            </div>
        );
    };

    render() {
        const { label, creatable, categoryIcons, disabled, className } = this.props;
        const SelectComponent = creatable ? Select.Creatable : Select;
        const value = this.props.selectedValue;
        const renderer = categoryIcons ? this.iconValueRenderer : null;

        return (
            <div className={classNames(s.selectFieldEditor, className, { [s.disabled]: disabled })}>
                {label && <div className={s.label}>{label.toUpperCase()}</div>}
                <SelectComponent value={value} clearable={false} onChange={this.handleChange} options={this.enhancedOptions()} optionRenderer={renderer} valueRenderer={renderer} disabled={disabled} />
            </div>
        );
    }
}
